/**
 * nodeStatus — plain-English names for a brain note's lifecycle status.
 *
 * Every note in an agent's brain carries a `status` of `active | testing |
 * retired` (the frontmatter schema in CLAUDE.md). Those are engineer words: a
 * non-technical founder reading a chip that just says "testing" can't tell
 * whether the note is being tested, is test data, or needs testing. The Brain
 * Hub is where founders browse and tidy those notes, so it must speak plainly.
 *
 * `nodeStatusLabel` maps each status to one short chip phrase; `nodeStatusTip`
 * gives the one-line explanation used for the status dot's hover title and its
 * screen-reader / touch-accessible label (the dot was previously a colour with
 * a raw-slug hover title — invisible on a phone, meaningless to a founder).
 * Anything unrecognised falls back to a de-slugged Title-case string so a new
 * status value never leaks a raw slug.
 *
 * Kept dependency-free so it is unit-tested directly (tests/node-status.test.ts).
 */

/** status code → short chip phrase a founder can read. */
const STATUS_LABELS: Record<string, string> = {
  active: 'In use',
  testing: 'On trial',
  retired: 'Retired',
};

/** status code → one-line plain explanation (dot tooltip + accessible label). */
const STATUS_TIPS: Record<string, string> = {
  active: 'In use — the agent relies on this note.',
  testing: 'On trial — being tried out, not fully trusted yet.',
  retired: 'Retired — kept for the record, no longer used.',
};

/** De-slug an unknown status: 'foo-bar' → 'Foo bar'. */
function deslug(s: string): string {
  const words = s.replace(/[-_]+/g, ' ').trim();
  if (!words) return s;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Short, founder-readable name for a brain note's status. */
export function nodeStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  const key = status.trim();
  return STATUS_LABELS[key] ?? deslug(key);
}

/** One-line explanation for a status, for tooltips and accessible labels. */
export function nodeStatusTip(status: string | null | undefined): string {
  if (!status) return 'Status unknown.';
  const key = status.trim();
  return STATUS_TIPS[key] ?? `Status: ${deslug(key)}.`;
}
